import { MouseEvent, useCallback, useMemo } from 'react';
import { localPoint } from '@visx/event';
import { useTooltip as usePrimitive } from '@visx/tooltip';

import { LineChartSeries } from '@components/patterns';
import { ANNOTATION_WIDTH } from '@components/patterns/LineChart/constants';
import { TooltipData } from '@components/patterns/LineChart/Tooltip';
import { UseScalesResult } from '@components/patterns/LineChart/use-scales';

export interface UseTooltipBaseResult {
  onPointerMove: (event: MouseEvent<SVGRectElement>) => void;
  onPointerLeave: () => void;
}

export interface UseTooltipClosedResult extends UseTooltipBaseResult {
  isOpen: false;
}

export interface UseTooltipOpenResult extends UseTooltipBaseResult {
  isOpen: true;
  data: TooltipData;
  left: number;
  top: number;
}

export type UseTooltipResult = UseTooltipClosedResult | UseTooltipOpenResult;

export function useTooltip(
  series: LineChartSeries<any, any>[],
  scales: UseScalesResult
): UseTooltipResult {
  const {
    tooltipOpen,
    tooltipData,
    showTooltip,
    hideTooltip,
    tooltipLeft,
    tooltipTop,
  } = usePrimitive<TooltipData>();

  const handleTooltipLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  const handleTooltipMove = useCallback(
    (event: MouseEvent<SVGRectElement>) => {
      const point = localPoint(event) || { x: 0, y: 0 };

      // If point is outside the index scale, hide the tooltip
      const index = Math.floor(scales.index.invert(point.x));
      const domain = scales.index.domain();
      if (index < domain[0] || index > domain[1]) {
        handleTooltipLeave();
        return;
      }

      // Find the closest annotation to the point
      const nearbyAnnotations = series?.flatMap(({ key, annotations }) => {
        const indices = annotations?.flatMap((annotation, index) => {
          const annotationX = scales.x(annotation.x);
          const distance = Math.abs(annotationX - point.x);
          if (distance < ANNOTATION_WIDTH / 2) {
            return { seriesKey: key, index, distance };
          }
          return [];
        });
        const closest = indices?.sort((a, b) => a.distance - b.distance).at(0);
        return closest ?? [];
      });
      const closestAnnotation = nearbyAnnotations
        ?.sort((a, b) => a.distance - b.distance)
        .at(0);

      const tooltipData: TooltipData = {
        index,
      };
      if (closestAnnotation) {
        tooltipData.annotation = {
          seriesKey: closestAnnotation.seriesKey,
          index: closestAnnotation.index,
        };
      }

      showTooltip({
        tooltipData,
        tooltipLeft: point.x,
        tooltipTop: point.y,
      });
    },
    [showTooltip, handleTooltipLeave, scales, series]
  );

  return useMemo(() => {
    const base: UseTooltipBaseResult = {
      onPointerMove: handleTooltipMove,
      onPointerLeave: handleTooltipLeave,
    };

    if (!tooltipOpen || !tooltipData) {
      return {
        ...base,
        isOpen: false,
      };
    } else {
      return {
        ...base,
        isOpen: true,
        data: tooltipData,
        left: tooltipLeft ?? 0,
        top: tooltipTop ?? 0,
      };
    }
  }, [
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    handleTooltipMove,
    handleTooltipLeave,
  ]);
}
